import type { Hit, Mode } from "@ranking-arena/shared/schemas";

import { ResultCard } from "./ResultCard";

interface ResultColumnsProps {
  hitsLeft: Hit[];
  hitsRight: Hit[];
  latencyLeft: number | null;
  latencyRight: number | null;
  density: "comfortable" | "compact";
  mode: Mode;
}

/**
 * Blind dual-column result view. Left/Right are neutral slots — the
 * server-assigned `leftIsProfile1` flip means the user can't tell which
 * column corresponds to profile A or B until they've voted.
 *
 * Layout: a single CSS Grid with two columns. Each rank emits a paired
 * row of two cells (Left[i], Right[i]) so rank-N on both sides shares a
 * row whose height = max of the two cards' natural heights. One outer
 * scroll container; no JS scroll-sync needed because there's only one
 * scroller. SideHeader cells in row 1 are sticky-pinned to the top.
 *
 * Asymmetric lengths (one side has more hits than the other): trailing
 * rows render the populated card paired with an empty placeholder div
 * so the grid stays aligned and React keeps stable keys.
 */
export function ResultColumns({
  hitsLeft,
  hitsRight,
  latencyLeft,
  latencyRight,
  density,
  mode,
}: ResultColumnsProps) {
  const rows = Math.max(hitsLeft.length, hitsRight.length);
  const pairs: Array<[Hit | undefined, Hit | undefined]> = Array.from({ length: rows }, (_, i) => [
    hitsLeft[i],
    hitsRight[i],
  ]);

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", paddingRight: 4 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: 16,
          rowGap: 8,
        }}
      >
        <SideHeader side="left" count={hitsLeft.length} latency={latencyLeft} />
        <SideHeader side="right" count={hitsRight.length} latency={latencyRight} />
        {pairs.flatMap(([left, right], i) => [
          left ? (
            <ResultCard
              key={`l-${left.id}-${i}`}
              hit={left}
              rank={i + 1}
              density={density}
              mode={mode}
            />
          ) : (
            <div key={`l-empty-${i}`} />
          ),
          right ? (
            <ResultCard
              key={`r-${right.id}-${i}`}
              hit={right}
              rank={i + 1}
              density={density}
              mode={mode}
            />
          ) : (
            <div key={`r-empty-${i}`} />
          ),
        ])}
      </div>
    </div>
  );
}

function SideHeader({
  side,
  count,
  latency,
}: {
  side: "left" | "right";
  count: number;
  latency: number | null;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 4px 12px",
        borderBottom: "1px solid var(--border)",
        position: "sticky",
        top: 0,
        background: "var(--background)",
        zIndex: 1,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: "var(--secondary)",
          border: "1px solid var(--border)",
          color: "var(--muted-foreground)",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-mono)",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.05em",
        }}
      >
        {side === "left" ? "L" : "R"}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{side === "left" ? "Left" : "Right"}</div>
      <div style={{ flex: 1 }} />
      {latency != null && (
        <div
          style={{
            fontSize: 11,
            color: "var(--muted-foreground)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {latency} ms
        </div>
      )}
      <div
        style={{
          fontSize: 11,
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
        }}
      >
        {count} hits
      </div>
    </div>
  );
}
